export const fileToBase64 = (file: Blob) =>
	new Promise((resolve, reject) => {
		let reader = new FileReader();
		reader.readAsDataURL(file);

		reader.onload = () => {
			resolve(reader.result);
		};

		reader.onerror = (error) => {
			console.error("Error: ", error);
			reject(error);
		};
	});

export const getUniqueArray = (array: Array<any>) =>
	array.filter((v, i, a) => a.indexOf(v) === i);

export const isBlast = import.meta.env.VITE_MODE === "blast";
export const isTech = window.location.origin.includes("igneous.tech");
export const isTest = window?.location?.origin?.includes("-test.");
export const testStyles = isTest
	? {
			borderColor: "fuchsia",
			borderWidth: "thick",
			borderStyle: "dashed",
		}
	: {};

export const mailRegex =
	/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/;

export const flattenObject = (ob: any, isDeleteImages = true) => {
	const toReturn = {} as any;

	for (const i in ob) {
		if (
			ob[i] === undefined ||
			(isDeleteImages &&
				(i === "avatar_Base64" ||
					(typeof ob[i] === "string" &&
						ob[i].startsWith("data:image"))))
		) {
			delete ob[i];
			continue;
		}

		if (!ob.hasOwnProperty(i)) continue;

		if (typeof ob[i] == "object" && ob[i] !== null) {
			const flatObject = flattenObject(ob[i]);
			for (const x in flatObject) {
				if (!flatObject.hasOwnProperty(x)) continue;

				toReturn[i + "." + x] = flatObject[x];
			}
		} else {
			toReturn[i] = ob[i];
		}
	}
	return toReturn;
};

export const pathRouter = (orgId: string) => {
	const path = window.location.pathname;
	const regex = new RegExp(`\/org\/[^\/]*`);
	return regex.test(path)
		? path.replace(regex, `/org/${orgId}`)
		: `/org/${orgId}${path}`;
};

// Build a fully org-scoped app path up front. App pages live under
// "/org/:orgId/...". Linking/navigating to a bare path (e.g. "/offerings")
// lands on a route with no :orgId param, so ChangeOrg bounces it to the
// org-scoped form via <Navigate>. Under react-router v7 that <Navigate>
// redirects inside a useEffect, so it first renders null — the whole layout
// unmounts for one frame, a white flash on navigation. Prefixing the org
// segment ourselves skips the redirect entirely. Falls back to the bare path
// when no org is selected yet. (See sideNavItem.js for the original fix.)
export const orgPath = (orgId: string | undefined, path = "") =>
	orgId ? joinUrl("/org", orgId, path) : path;

// Site pages live one level deeper, under "/org/:orgId/site/:siteId/...".
// Same flash rationale as orgPath; falls back to the bare "/site/:siteId" form
// when no org is known yet.
export const sitePath = (
	orgId: string | undefined,
	siteId: string | undefined,
	path = ""
) =>
	orgId
		? joinUrl("/org", orgId, "site", siteId, path)
		: joinUrl("/site", siteId, path);

// Join URL segments with single "/"s, regardless of stray leading/trailing
// slashes on any segment (so callers don't have to care). Empty/undefined
// segments are dropped. The first segment keeps its leading slash so absolute
// app paths (e.g. "/org/...") stay absolute.
export const joinUrl = (...segments: Array<string | undefined>) =>
	segments
		.filter((segment): segment is string => Boolean(segment))
		.map((segment, i) =>
			i === 0
				? segment.replace(/\/+$/, "") // first: trim trailing only
				: segment.replace(/^\/+|\/+$/g, "")
		)
		.filter(Boolean)
		.join("/");

export const urlSlicer = (url: string) =>
	joinUrl(window.location.pathname, url);

export const blastHref = (blastId: string) =>
	"terra://" +
	(isTech
		? import.meta.env.VITE_BLAST_API_URL_TECH
		: import.meta.env.VITE_BLAST_API_URL
	)
		?.split("//")[1]
		.split("/")[0] +
	"/" +
	blastId;

export const BLST_EXTENSION = ".blst";

export const JSONToFile = (
	obj: string,
	filename: string,
	extension = ".json" as string,
) => {
	const blob = new Blob([obj], {
		type: "application/json",
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename + extension;
	a.click();
	URL.revokeObjectURL(url);
};

export const binaryToFile = (
	buffer: ArrayBuffer,
	filename: string,
	extension = ".bin" as string,
) => {
	const blob = new Blob([buffer]);
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename + extension;
	a.click();
	URL.revokeObjectURL(url);
};

/**
 * Returns the browser locale's date field order as a moment-compatible
 * format string, e.g. "MM/DD/YYYY" (US), "DD/MM/YYYY" (AU/most),
 * "YYYY/MM/DD" (ISO-order locales) — including the locale's real separator
 * ("/", "-", "."). Use it for both displaying and parsing typed dates so the
 * field order always matches what the user expects, instead of relying on
 * moment's US-biased default (which read 01/06/2026 as 6 January).
 *
 * Pinned to the Gregorian calendar so locales that default to a non-Gregorian
 * calendar (e.g. ar-SA, fa-IR) still yield the order they use for Gregorian
 * dates. Computed from Intl, so it's correct for locales a `navigator.language`
 * region check would miss. Falls back to "DD/MM/YYYY" if Intl throws.
 *
 * Pass no argument to use the browser's locale (the common case — see the
 * exported DATE_FORMAT const); pass a locale only when you need a specific one.
 */
export const getLocaleDateFormat = (locale?: string): string => {
	try {
		// Dec 31 2000 disambiguates day (31) from month (12) from year (2000).
		const parts = new Intl.DateTimeFormat(locale, {
			calendar: "gregory",
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}).formatToParts(new Date(2000, 11, 31));

		return parts
			.map((part) => {
				switch (part.type) {
					case "day":
						return "DD";
					case "month":
						return "MM";
					case "year":
						return "YYYY";
					default:
						return part.value; // separator, e.g. "/" "-" "."
				}
			})
			.join("");
	} catch {
		return "DD/MM/YYYY";
	}
};

/**
 * The browser locale's date format, computed once. Import this directly when
 * you just need the format string; call getLocaleDateFormat() only when you
 * need a specific (non-default) locale.
 */
export const DATE_FORMAT = getLocaleDateFormat(navigator.language);
